import { useEffect, useRef, useState } from 'react';

const WORKATO_ORIGI_REGEX = /workato\.com$/;
/**
 * Hook to handle Workato iframe connection and postMessage events.
 *
 * Listens for postMessage events from the Workato embedded connection widget
 * and provides dynamic iframe height based on heightChange events.
 *
 * @returns An object containing the iframe ref and current height
 *
 * @example
 * ```tsx
 * function WorkatoModal() {
 *   const { iframeRef, iframeHeight } = useWorkatoIframeConnection();
 *
 *   return (
 *     <iframe
 *       ref={iframeRef}
 *       height={iframeHeight}
 *       src={workatoUrl}
 *     />
 *   );
 * }
 * ```
 */
export function useWorkatoIframeConnection({
  enabled = true,
}: {
  /**
   * Whether to enable the iframe connection.
   */
  enabled?: boolean;
}) {
  const [iframeHeight, setIframeHeight] = useState(640);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!WORKATO_ORIGI_REGEX.test(event.origin)) {
        return;
      }

      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.wk !== true) {
          // A secondary check to ensure the message is from Workato
          return;
        }

        switch (data.type) {
          case 'heightChange':
            setIframeHeight(data.payload.height);
            return;
          case 'connectionStatusChange':
            // TODO(sleahy): Handle connection status change
            return;
          case 'error':
            // TODO(sleahy): Add telemetry for iframe errors
            console.error('Workato iframe error:', data.payload.error);
            return;
        }
      } catch {
        // Ignore invalid messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [enabled]);

  return { iframeRef, iframeHeight };
}
